import { Container, Typography } from "@mui/material";
import { useVirtualizer } from "@tanstack/react-virtual";
import React, { Fragment, useRef } from "react";
import { useTranslation } from "react-i18next";
import { GiSniffingDog } from "react-icons/gi";
import { useLocation } from "react-router-dom";
import SimpleAlert from "../../../../../components/display/SimpleAlert";
import FullScreenMessageCore from "../../../../../components/fullScreenMessage/FullScreenMessageCore";
import Skeletons from "../../../../../components/loader/Skeletons";
import BookingModal from "../../../../../components/modals/bookingModalComponents/components/BookingModal";
import BookingModalContext from "../../../../../components/modals/bookingModalComponents/context/BookingModalContext";
import CustomDialog from "../../../../../components/wrappers/CustomDrawer";
import NoClinicWrapper from "../../../../../components/wrappers/NoClinicWrapper";
import ToggleDrawer from "../../../../../contexts/ToggleDrawer";
import usePrimaryColor from "../../../../../hooks/usePrimaryColor";
import SingleAppointment from "../../display/SingleAppointment";

export default function AppointmentMapper({
  appointments,
  title = "NoAppointmentsNoThisTab",
  isPetDeceased = false,
  isOwnerDeleted = false,
}) {
  const color = usePrimaryColor();
  const { t } = useTranslation();

  if ((appointments == null || appointments?.length < 1) && !isOwnerDeleted) {
    return (
      <Container
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          height: "50vh",
        }}
      >
        <FullScreenMessageCore
          icon={<GiSniffingDog size={75} color={color} />}
          title={title}
          withoutWrapper
          shouldCenter={true}
        >
          {isPetDeceased ? (
            <Typography>{t("PetDeceasedBookingNotAvailable")}</Typography>
          ) : (
            <NoClinicWrapper>
              <ToggleDrawer>
                <CustomDialog
                  buttonType={"contained"}
                  sx={{ bgcolor: "white" }}
                  buttonMessage={t("Book")}
                  buttonSize={"large"}
                  maxWidth={"lg"}
                >
                  <BookingModalContext key={"appointment-booking-key"}>
                    <BookingModal key={"appointment-booking-modal"} />
                  </BookingModalContext>
                </CustomDialog>
              </ToggleDrawer>
            </NoClinicWrapper>
          )}
        </FullScreenMessageCore>
      </Container>
    );
  }

  return (
    <Fragment>
      {appointments.map((item, index) => (
        <SingleAppointment
          appointment={item}
          key={index}
          isOwnerDeleted={isOwnerDeleted}
        />
      ))}
    </Fragment>
  );
}

const defaultValues = "defaultPageValues";

function getSizeEstimate(currentPath) {
  const matchedKey = Object.keys(estimateByPage).find((key) =>
    currentPath.includes(key),
  );
  if (matchedKey) {
    return estimateByPage[matchedKey];
  }
  return estimateByPage[defaultValues];
}

const estimateByPage = {
  "/pets/pet/": {
    pageSize: "65vh",
    estimateSize: 135,
    fullWidth: true, // the single pet view's tab already sets the page width
  },
  defaultPageValues: {
    pageSize: "75vh",
    estimateSize: 120,
  },
};

export function AppointmentMapperInfinite({
  appointments,
  title = "NoAppointmentsNoThisTab",
  fetchNextPage,
  isFetchingNextPage,
  isFetchNextPageError,
  hasNextPage,
  containerHeight,
  isOwnerDeleted = false,
}) {
  const scrollRef = useRef(null);
  const color = usePrimaryColor();
  const location = useLocation();
  const pageValues = getSizeEstimate(location.pathname);

  const virtualizer = useVirtualizer({
    count: appointments?.length ?? 0,
    estimateSize: () => pageValues?.estimateSize, //How tall are the single appointment components
    getScrollElement: () => scrollRef.current,
  });

  const virtualItems = virtualizer.getVirtualItems();

  React.useEffect(() => {
    const lastItem = virtualItems[virtualItems.length - 1];
    if (!hasNextPage || isFetchingNextPage || !lastItem) return;
    if (lastItem.index >= appointments.length - 1) {
      fetchNextPage();
    }
  }, [
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    appointments,
    virtualItems,
  ]);

  if (!appointments || Number(appointments.length) === 0) {
    return (
      <Container sx={{ mt: 2 }} data-testid={"appointment.user-tabs.empty-message"}>
        <FullScreenMessageCore
          centerRelative={false}
          withoutWrapper
          shouldCenter={true}
          icon={<GiSniffingDog size={75} color={color} />}
          title={title}
        />
      </Container>
    );
  }
  const heightStyle = containerHeight
    ? `${containerHeight}`
    : (pageValues?.pageSize ?? "70vh");

  return (
    <div
      ref={scrollRef}
      data-testid={"appointment.user-tabs.scroll-container"}
      style={{
        height: heightStyle, // or some fixed height
        overflowY: "auto", // critical for virtualization!
      }}
    >
      <div
        style={{
          position: "relative",
          height: `${virtualizer.getTotalSize()}px`,
        }}
      >
        {virtualItems?.map((vItem) => {
          const item = appointments?.[vItem.index];
          if (!item) return null;
          return (
            <div
              key={vItem.key}
              style={{
                transform: `translateY(${vItem.start}px)`,
                height: `${vItem.size}px`,
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
              }}
            >
              <SingleAppointment appointment={item} key={item.id} isOwnerDeleted={isOwnerDeleted} fullWidth={pageValues?.fullWidth}/>
            </div>
          );
        })}
      </div>

      {isFetchNextPageError && (
        <Container sx={{ mt: 2 }}>
          <SimpleAlert message="ErrorFetchingData" severity="error" />
        </Container>
      )}

      {hasNextPage && (
        <>
          {isFetchingNextPage && (
            <Container sx={{ mt: 2 }}>
              <Skeletons
                numOfSkeletons={3}
                grid={false}
                height={90}
                spacing={2}
              />
            </Container>
          )}
        </>
      )}
    </div>
  );
}
